import { galleryCard } from "./gallery-data";
import { stockCard, PHOTO } from "./stock-images";

export interface ServiceData {
  slug: string;
  title: string;
  subtitle: string;
  price: string;
  image: string;
  description: string;
  features: string[];
  process: { step: string; desc: string }[];
}

export const services: ServiceData[] = [
  {
    slug: "deep-cleaning",
    title: "Deep Cleaning",
    subtitle: "Top-to-bottom reset for your whole space",
    price: "Free Estimate",
    image: stockCard(PHOTO.deepCleanDetail),
    description:
      "Our deep clean goes far past a routine tidy-up. We work room by room through the details that build up over months — baseboards, door frames, switch plates, vent covers, ceiling fans, inside appliances, grout lines, and behind furniture. It is the ideal first visit before starting a recurring plan, and the perfect reset before guests, a holiday, or a sale.",
    features: [
      "Baseboards, trim, door frames & switch plates",
      "Inside oven, microwave & refrigerator on request",
      "Tile, grout & bathroom buildup removal",
      "Ceiling fans, vents, light fixtures & blinds",
      "Interior windows, sills & tracks",
      "Detailed hand-wiping, not just surface spraying",
    ],
    process: [
      { step: "Walkthrough", desc: "We review every room with you and flag the areas that need the most attention." },
      { step: "Custom Plan", desc: "You get a written scope and an honest price before we start any work." },
      { step: "Deep Clean", desc: "Our team works top-to-bottom, room by room, following a fixed checklist." },
      { step: "Final Check", desc: "We walk the space with you and touch up anything that isn't right." },
    ],
  },
  {
    slug: "residential-cleaning",
    title: "Residential Cleaning",
    subtitle: "Houses, condos & apartments",
    price: "Free Estimate",
    image: stockCard(PHOTO.tidyLivingRoom),
    description:
      "Home cleaning built around how you actually live. We handle kitchens, bathrooms, bedrooms, living areas, floors, and every high-touch surface in between, so you come home to a space that feels genuinely finished. Families across Orange Park, Fleming Island, Mandarin, and the wider Jacksonville area book us weekly, bi-weekly, monthly, or as a one-time refresh.",
    features: [
      "Full kitchen: counters, sinks, cabinet fronts & appliance exteriors",
      "Bathrooms scrubbed, sanitized & polished",
      "Bedrooms, living areas & beds made",
      "Vacuuming, sweeping & mopping throughout",
      "Dusting from ceiling fans down to baseboards",
      "Trash removed and liners replaced",
    ],
    process: [
      { step: "Tell Us Your Home", desc: "Share bedrooms, bathrooms, square footage, and any pets so we can quote accurately." },
      { step: "Free Estimate", desc: "We send clear pricing with no hidden fees or surprise add-ons." },
      { step: "We Clean", desc: "Our team arrives on schedule with our own supplies and equipment." },
      { step: "Stay on Schedule", desc: "Lock in a recurring slot and keep the same trusted team each visit." },
    ],
  },
  {
    slug: "recurring-cleaning",
    title: "Weekly, Bi-Weekly & Monthly Cleaning",
    subtitle: "Recurring service that keeps it that way",
    price: "Free Estimate · Repeat Discount",
    image: stockCard(PHOTO.organizedCleaningTools),
    description:
      "The easiest way to never think about cleaning again. Pick the rhythm that fits your household or business — weekly, every two weeks, or once a month — and we handle the rest on a fixed schedule with the same checklist every visit. Recurring customers get priority booking and a discount on every clean.",
    features: [
      "Weekly, bi-weekly, or monthly schedules",
      "Same checklist and consistent quality each visit",
      "Priority booking windows",
      "Discounted rate for returning customers",
      "Easy rescheduling around holidays and travel",
      "Add deep-clean or laundry service any time",
    ],
    process: [
      { step: "Pick a Frequency", desc: "Weekly, bi-weekly, or monthly — whatever matches your household." },
      { step: "Set the Scope", desc: "We agree on exactly which rooms and tasks are covered every visit." },
      { step: "Recurring Visits", desc: "We show up on the same day and window each cycle, no reminders needed." },
      { step: "Adjust Any Time", desc: "Add rooms, skip a week, or change frequency with a quick call or text." },
    ],
  },
  {
    slug: "move-in-move-out-cleaning",
    title: "Move In / Move Out Cleaning",
    subtitle: "Empty-home deep clean for a clean handover",
    price: "Free Estimate",
    image: stockCard(PHOTO.emptyCleanedRoom),
    description:
      "Moving is stressful enough. We handle the clean so you can get your deposit back or walk into a new place that feels genuinely fresh. We work best on an empty property, cleaning inside cabinets and drawers, appliances, closets, baseboards, and every corner that furniture normally hides — finished to the standard landlords and property managers expect.",
    features: [
      "Inside all cabinets, drawers & closets",
      "Inside oven, refrigerator & dishwasher",
      "Bathrooms fully sanitized top to bottom",
      "Baseboards, door frames, walls spot-cleaned",
      "Floors vacuumed and mopped throughout",
      "Ideal for tenants, landlords & realtors",
    ],
    process: [
      { step: "Share the Details", desc: "Tell us the size of the property and your closing or handover date." },
      { step: "Book Your Window", desc: "We schedule around your movers so the space is empty when we arrive." },
      { step: "Full Deep Clean", desc: "Every cabinet, appliance, and corner is cleaned to move-out standard." },
      { step: "Walkthrough", desc: "We do a final pass so the property is inspection-ready." },
    ],
  },
  {
    slug: "office-cleaning",
    title: "Office Cleaning",
    subtitle: "Healthy, professional workspaces",
    price: "Free Estimate",
    image: stockCard(PHOTO.officeInterior),
    description:
      "A clean office is the first thing your clients notice and the last thing your team should worry about. We service private offices, open floors, coworking spaces, conference rooms, break rooms, and restrooms on a schedule that works around your business — including early mornings, evenings, and weekends.",
    features: [
      "Desks, workstations & private offices",
      "Conference rooms, lobbies & reception areas",
      "Break rooms, kitchens & coffee stations",
      "Restrooms sanitized and restocked",
      "Trash, recycling & high-touch disinfection",
      "Before-hours and after-hours scheduling",
    ],
    process: [
      { step: "Office Walkthrough", desc: "We assess square footage, headcount, and traffic patterns on site." },
      { step: "Custom Quote", desc: "You get flat, predictable pricing based on scope and frequency." },
      { step: "Scheduled Service", desc: "We clean around your hours so your team is never interrupted." },
      { step: "Ongoing Quality", desc: "Regular check-ins make sure standards hold visit after visit." },
    ],
  },
  {
    slug: "commercial-cleaning",
    title: "Commercial Cleaning",
    subtitle: "Retail, restaurants & storefronts",
    price: "Free Estimate",
    image: stockCard(PHOTO.emptyRestaurant),
    description:
      "Keep your business floor-ready every single day. We clean retail spaces, restaurants, salons, gyms, and storefronts across Clay, Duval, and St. Johns counties, scheduling around your open hours so floors shine, restrooms are spotless, and customers walk into a space that reflects your standards.",
    features: [
      "Daily, nightly & weekend availability",
      "Floors, restrooms, counters & customer areas",
      "Food-prep and back-of-house sanitization",
      "Glass, windows & high-touch disinfection",
      "Post-construction and post-event cleanup",
      "Flexible contracts, no long lock-ins",
    ],
    process: [
      { step: "Site Visit", desc: "We tour your space to understand traffic, surfaces, and problem areas." },
      { step: "Clear Quote", desc: "A flat-rate proposal with the scope written out line by line." },
      { step: "Work Around You", desc: "We clean before open, after close, or overnight — your call." },
      { step: "Quality Audit", desc: "Routine spot-checks keep your space consistently presentable." },
    ],
  },
  {
    slug: "airbnb-turnover-cleaning",
    title: "Airbnb & Short-Term Rental Cleaning",
    subtitle: "Fast turnovers, five-star ready",
    price: "Free Estimate",
    image: galleryCard(27),
    description:
      "Guest reviews live and die on cleanliness. We turn over Airbnb, VRBO, and short-term rentals across Orange Park, Jacksonville, Fleming Island, and St. Johns County between check-out and check-in — full clean, fresh linens, restock, and a staged finish that photographs exactly like your listing.",
    features: [
      "Same-day turnovers between guests",
      "Linens stripped, washed & beds restaged",
      "Bathrooms sanitized and towels replaced",
      "Amenity and supply restocking",
      "Damage and missing-item reporting with photos",
      "Flexible scheduling for back-to-back bookings",
    ],
    process: [
      { step: "Share Your Calendar", desc: "Give us your booking windows so turnovers never run late." },
      { step: "Turnover Checklist", desc: "We build a per-property checklist matched to your listing photos." },
      { step: "Clean & Stage", desc: "Full clean, fresh linens, restock, and a staged final look." },
      { step: "Photo Confirmation", desc: "We send photos so you know the property is guest-ready." },
    ],
  },
  {
    slug: "clinic-and-medical-cleaning",
    title: "Clinic & Medical Office Cleaning",
    subtitle: "Sanitation standards patients notice",
    price: "Free Estimate",
    image: stockCard(PHOTO.clinicExamRoom),
    description:
      "Medical spaces demand a higher standard. We clean clinics, dental offices, therapy practices, and medical suites with a disinfection-first process for waiting rooms, exam rooms, restrooms, and every high-touch surface between them — scheduled around patient hours so your practice never pauses.",
    features: [
      "Waiting rooms, reception & check-in areas",
      "Exam rooms and treatment spaces",
      "Hospital-grade disinfectants on high-touch points",
      "Restrooms sanitized and restocked",
      "Floors detailed and disinfected",
      "After-hours scheduling around patient load",
    ],
    process: [
      { step: "Practice Walkthrough", desc: "We map exam rooms, common areas, and your disinfection priorities." },
      { step: "Protocol Plan", desc: "A written cleaning protocol tailored to your practice type." },
      { step: "Scheduled Service", desc: "We work before opening or after close so patients are never disrupted." },
      { step: "Consistent Standards", desc: "The same protocol runs every visit, with regular quality checks." },
    ],
  },
  {
    slug: "closet-organization",
    title: "Closet Organization",
    subtitle: "Sorted, folded & genuinely usable",
    price: "Free Estimate",
    image: stockCard(PHOTO.organizedCloset),
    description:
      "A closet you can actually use. We empty, clean, sort, and rebuild closets, pantries, and storage spaces into a layout that makes sense — grouped by category, folded properly, and arranged so everything is visible at a glance. Perfect on its own or paired with a deep clean.",
    features: [
      "Full empty-out, wipe-down & reset",
      "Sorting by category, season & frequency of use",
      "Professional folding and hanging",
      "Pantry, linen closet & storage room organization",
      "Donation and discard piles separated for you",
      "Layouts designed to stay organized",
    ],
    process: [
      { step: "Assess the Space", desc: "We look at what you own and how you actually use the space." },
      { step: "Empty & Clean", desc: "Everything comes out, and the closet itself gets fully cleaned." },
      { step: "Sort & Rebuild", desc: "Items are grouped, folded, and arranged into a usable layout." },
      { step: "Walk You Through", desc: "We show you the system so it stays organized after we leave." },
    ],
  },
  {
    slug: "laundry-service",
    title: "Laundry Service",
    subtitle: "Washed, dried, folded & put away",
    price: "Free Estimate",
    image: stockCard(PHOTO.laundryLoadingWasher),
    description:
      "Hand back your least favorite chore. We wash, dry, fold, and put away laundry as a standalone service or as an add-on to any cleaning visit — everyday loads, bedding and linens, or short-term rental turnovers. Fabrics are sorted and handled according to their care labels.",
    features: [
      "Wash, dry, fold & put away",
      "Sorted by fabric, color & care label",
      "Bedding, towels & linens included",
      "Beds stripped and remade with fresh sheets",
      "Available as an add-on to any clean",
      "Ideal for rentals and busy households",
    ],
    process: [
      { step: "Tell Us the Load", desc: "Let us know volume, fabrics, and any special care instructions." },
      { step: "Sort & Wash", desc: "Everything is separated by color and fabric before washing." },
      { step: "Dry & Fold", desc: "Dried on the right setting, then folded or hung properly." },
      { step: "Put Away", desc: "Clothes returned to drawers and closets, beds remade with fresh linens." },
    ],
  },
];
